import { Card, BlockStack, Select, Combobox, Listbox, Text, Box, InlineStack, Spinner, Thumbnail, Banner } from '@shopify/polaris';
import { SEARCH_TYPE_OPTIONS } from '#client/components/features/LabelPrinting/utils/styleSearch';

export default function StyleSearchField({
    locationOptions,
    locationGid,
    onLocationChange,
    searchType,
    onSearchTypeChange,
    term,
    onTermChange,
    isLoading,
    matches,
    hasMore,
    error,
    resolveError,
    showResults,
    onSelect,
}) {
    // The dropdown opens only when it actually has something to show. A search failure
    // is reported by the Banner below, in the card, not in here — a popover closes on
    // blur, and a persistent failure the operator needs to act on must not vanish the
    // moment they click away. While a search is in flight with nothing to show yet
    // (no prior matches to keep displaying), the dropdown stays closed rather than
    // opening on an empty Listbox.
    const showListbox = showResults && !error && (matches.length > 0 || !isLoading);

    const handleListboxSelect = (value) => {
        const match = matches.find((candidate) => candidate.productId === value);
        if (match) onSelect(match);
    };

    const listboxMarkup = showListbox ? (
        <Listbox accessibilityLabel="Style search results" onSelect={handleListboxSelect}>
            {matches.length === 0 ? (
                <Listbox.TextOption disabled>No styles match that search.</Listbox.TextOption>
            ) : (
                matches.map((match) => (
                    <Listbox.Option
                        key={match.productId}
                        value={match.productId}
                        accessibilityLabel={`${match.title}${match.vendorPartNumber ? `, part number ${match.vendorPartNumber}` : ''}, ${match.variantCount} variants`}
                    >
                        {/* TextOption is what actually supplies row padding and the
                            hover/keyboard-focus background — Option itself renders with
                            no padding or affordance at all. It has to be the direct
                            child of Option; Option only auto-wraps a plain string. */}
                        <Listbox.TextOption>
                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                                <Thumbnail source={match.image || ''} alt={match.title} size="small" />
                                <BlockStack gap="050">
                                    <Text as="span" fontWeight="semibold">
                                        {match.title}
                                    </Text>
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        {match.vendorPartNumber ? `Part number: ${match.vendorPartNumber} · ` : ''}
                                        {match.variantCount} variants
                                    </Text>
                                </BlockStack>
                            </InlineStack>
                        </Listbox.TextOption>
                    </Listbox.Option>
                ))
            )}
            {hasMore && <Listbox.TextOption disabled>Showing the first 50 matches. Keep typing to narrow them.</Listbox.TextOption>}
        </Listbox>
    ) : null;

    return (
        <Card>
            <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                    Find a style
                </Text>

                <Select label="Store" options={locationOptions} value={locationGid || ''} onChange={onLocationChange} helpText="Prefilled from your selected location." />

                <InlineStack gap="300" blockAlign="end" wrap={false}>
                    <Box minWidth="220px">
                        <Select label="Search type" options={SEARCH_TYPE_OPTIONS} value={searchType} onChange={onSearchTypeChange} />
                    </Box>
                    <Box width="100%">
                        <Combobox
                            activator={
                                <Combobox.TextField
                                    label="Search"
                                    labelHidden
                                    value={term}
                                    onChange={onTermChange}
                                    autoComplete="off"
                                    placeholder="Search by child SKU, child UPC, or vendor part number"
                                    connectedRight={isLoading ? <Spinner accessibilityLabel="Searching" size="small" /> : undefined}
                                />
                            }
                        >
                            {listboxMarkup}
                        </Combobox>
                    </Box>
                </InlineStack>

                {resolveError && (
                    // A resolve failure (loading the chosen style's variants) is distinct
                    // from a search failure below: the search itself succeeded, so the
                    // list of matches is still valid and gives the operator a way to retry
                    // by selecting a style again. Stays in the card, not the popover, for
                    // the same reason the search-failure banner does (see below).
                    <Banner tone="critical">Could not load that style. Select it below to try again.</Banner>
                )}
                {error && (
                    // A failed search is not the same fact as a search that matched
                    // nothing — the "no results" copy in the dropdown reads as "this
                    // style doesn't exist" and would send an operator to check the shelf
                    // instead of retrying, so it must never render alongside a real
                    // failure. Kept in the card rather than the dropdown because a
                    // popover closes on blur, and a failure the operator needs to act on
                    // must not vanish when they click away.
                    <Banner tone="critical">Could not search for styles. Please try again.</Banner>
                )}
            </BlockStack>
        </Card>
    );
}
